import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Circle, Trophy, RotateCcw, BookOpen } from 'lucide-react';
import { useDashboardStore } from '../../stores/dashboardStore';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { useAuthStore } from '../../stores/authStore';
import ResetConfirmationModal from './ResetConfirmationModal';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

export default function ProgressOverview() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { progress, updateProgress } = useDashboardStore((state) => state);
  const { plan, resetsLeft, decrementResets } = useSubscriptionStore();
  const [showResetModal, setShowResetModal] = useState(false);
  const [hasPurchasedCourse, setHasPurchasedCourse] = useState(false);

  useEffect(() => {
    if (user?.id) {
      const purchased = localStorage.getItem(`purchased_courses_${user.id}`);
      if (purchased) {
        try {
          const parsedPurchased = JSON.parse(purchased);
          if (Array.isArray(parsedPurchased) && parsedPurchased.length > 0) {
            setHasPurchasedCourse(true);
          } else {
            setHasPurchasedCourse(false);
          }
        } catch (error) {
          console.error("Błąd parsowania zakupionych kursów z localStorage:", error);
          setHasPurchasedCourse(false);
        }
      } else {
        setHasPurchasedCourse(false);
      }
    }
  }, [user?.id]);

  const steps = [
    {
      title: 'Quiz kariery',
      completed: progress.quizCompleted,
      description: 'Podstawowa analiza predyspozycji',
      required: true,
      percentage: 50
    },
    {
      title: 'Analiza kariery',
      completed: progress.quizCompleted,
      description: plan === 'premium' 
        ? 'Pełna analiza dostępna' 
        : 'Podstawowa analiza (odblokuj pełną w Premium)',
      required: true,
      percentage: 25
    },
    {
      title: 'Rozwój i Edukacja',
      completed: hasPurchasedCourse,
      description: 'Zakup pierwszy kurs, aby kontynuować rozwój',
      required: false,
      percentage: 25
    }
  ];

  const calculateProgress = () => {
    let totalPercentage = 0;
    steps.forEach(step => {
      if (step.completed) {
        totalPercentage += step.percentage;
      }
    });
    return Math.min(100, totalPercentage);
  };

  const handleReset = async () => {
    if (resetsLeft <= 0) {
      toast.error('Wykorzystano limit resetów w tym miesiącu');
      return;
    }

    try {
      const userId = user?.id;
      if (userId) {
        localStorage.removeItem(`quiz_${userId}`);
        localStorage.removeItem(`analysis_${userId}`);
        localStorage.removeItem(`progress_${userId}`);
      }
      
      await updateProgress({
        quizCompleted: false,
        cvUploaded: false,
        profileCompleted: 0,
        applicationsSubmitted: 0,
        coursesStarted: 0,
        coursesCompleted: 0,
        analysisGenerated: false
      });

      setHasPurchasedCourse(false);

      decrementResets();

      toast.success('Możesz teraz wypełnić quiz ponownie');
      navigate('/#quiz');
    } catch (error) {
      console.error('Error resetting analysis:', error);
      toast.error('Wystąpił błąd podczas resetowania');
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          Twój Postęp
        </h2>
        <div className="flex items-center gap-4">
          {plan === 'premium' && progress.quizCompleted && (
            <button
              onClick={() => setShowResetModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              <span className="text-sm font-medium">Reset analizy</span>
            </button>
          )}
          <Trophy className="w-8 h-8 text-purple-600" />
        </div>
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-600">
            Ukończono: {calculateProgress()}%
          </span>
          <span className="text-sm text-gray-500">
            {plan === 'premium' ? 'Plan Premium' : 'Plan Darmowy'}
          </span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${calculateProgress()}%` }}
            className="h-full bg-purple-600"
          />
        </div>
      </div>

      <div className="space-y-6">
        {steps.map((step, index) => (
          <motion.div
            key={step.title}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="flex items-start gap-4"
          >
            {step.title === 'Rozwój i Edukacja' ? (
              step.completed ? (
                <CheckCircle className="w-6 h-6 text-green-500 mt-1" />
              ) : (
                <BookOpen className="w-6 h-6 text-gray-300 mt-1" />
              )
            ) : (
              step.completed ? (
                <CheckCircle className="w-6 h-6 text-green-500 mt-1" />
              ) : (
                <Circle className="w-6 h-6 text-gray-300 mt-1" />
              )
            )}
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium text-gray-700">
                    {step.title}
                  </span>
                </div>
                <span className="text-sm text-gray-500">
                  {step.completed ? 'Ukończone' : 'Do zrobienia'}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {step.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      <ResetConfirmationModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        onConfirm={handleReset}
        resetsLeft={resetsLeft}
      />
    </>
  );
}
